import React, { useState } from "react";
import { useSubscription } from "../../../contexts/SubscriptionContext";
import { getAccessToken } from "../../../awsAuth";
import Checkout from "./Checkout";
import "./SubscriptionUpsellModal.scss";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const PLAN_LABEL = { plus: "Cvanto Plus", business: "Cvanto Business" };

// One copy entry per upsell moment. `plan` is the default target plan for
// that reason — callers can still override it via the `plan` prop (e.g. a
// future "business_feature" upsell that specifically wants to promote
// Business even though Plus would also apply).
const REASON_COPY = {
  story_limit: {
    plan: "plus",
    title: "Ліміт активних Stories",
    body: "У Cvanto Free доступна 1 активна Story. У Cvanto Plus — до 5.",
  },
  remove_ads: {
    plan: "plus",
    title: "Не хочете бачити рекламу?",
    body: "Cvanto Plus — без реклами.",
  },
  business_feature: {
    plan: "business",
    title: "Розвивайте бізнес у Cvanto",
    body: "Каталог, онлайн-запис, вакансії та статистика бізнесу доступні на Cvanto Business.",
  },
  analytics: {
    plan: "plus",
    title: "Статистика доступна на Cvanto Plus",
    body: "Перегляди, збереження, вподобання та коментарі ваших оголошень — на Cvanto Plus і вище.",
  },
};

/**
 * Reusable contextual upsell, triggered right after a real user action hits
 * an entitlement wall (Story limit, a future ad-dismiss, a Business-only
 * feature) — never shown proactively/on a timer.
 *
 * Eligibility/active/used state always comes from SubscriptionContext
 * (backend-derived); this component never decides plan/trial state itself,
 * only reads it and lets the user act on it. Trial activation posts to the
 * same POST /api/subscription/trial the Subscription screen already uses,
 * then refreshes the shared context so every consumer (badges, limits,
 * ad-gating) picks up the new entitlements automatically. The
 * non-eligible path reuses the existing Checkout modal as-is — no new
 * payment logic here.
 */
export default function SubscriptionUpsellModal({
  open,
  onClose,
  reason = "story_limit",
  plan,
  onTrialActivated,
}) {
  const subscription = useSubscription();
  const trial = subscription?.trial || {};

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialError, setTrialError] = useState("");

  if (!open) return null;

  const copy = REASON_COPY[reason] || REASON_COPY.story_limit;
  const targetPlan = plan === "business" || plan === "plus" ? plan : copy.plan;
  const planLabel = PLAN_LABEL[targetPlan];

  if (checkoutOpen) {
    return <Checkout planKey={targetPlan} onClose={onClose} />;
  }

  const handleStartTrial = async () => {
    setTrialBusy(true);
    setTrialError("");

    try {
      const token = await getAccessToken?.().catch(() => null);

      if (!token) {
        setTrialError("Увійдіть у акаунт, щоб активувати пробний період.");
        return;
      }

      const res = await fetch(`${API_BASE}/api/subscription/trial`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan: targetPlan }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося активувати пробний період");
      }

      await subscription.refresh();
      onTrialActivated?.();
      onClose?.();
    } catch (e) {
      setTrialError(e.message || "Не вдалося активувати пробний період");
    } finally {
      setTrialBusy(false);
    }
  };

  return (
    <div className="upsell-overlay" onMouseDown={onClose}>
      <div className="upsell-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="upsell-close" onClick={onClose} aria-label="Закрити">
          ✕
        </button>

        <h2 className="upsell-title">{copy.title}</h2>
        <p className="upsell-body">{copy.body}</p>

        {trialError && <div className="upsell-error">{trialError}</div>}

        {trial.eligible ? (
          <>
            <button
              type="button"
              className="upsell-primary"
              disabled={trialBusy}
              onClick={handleStartTrial}
            >
              {trialBusy ? "Активуємо…" : `Спробувати ${planLabel} 7 днів безкоштовно`}
            </button>
            <p className="upsell-hint">7 днів безкоштовно. Без автоматичного списання.</p>
          </>
        ) : (
          <button
            type="button"
            className="upsell-primary"
            onClick={() => setCheckoutOpen(true)}
          >
            {`Перейти на ${planLabel}`}
          </button>
        )}

        <button type="button" className="upsell-secondary" onClick={onClose}>
          Не зараз
        </button>
      </div>
    </div>
  );
}
